import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import trainingService from '@/services/trainingService';
import {
  CreateTrainingGuideRequest,
  TrainingCategory,
  DifficultyLevel,
  UserRole,
  ContentType
} from '@/types/training';

const CATEGORIES: { value: TrainingCategory; label: string }[] = [
  { value: 'getting-started', label: 'Getting Started' },
  { value: 'platform-basics', label: 'Platform Basics' },
  { value: 'client-management', label: 'Client Management' },
  { value: 'loan-processing', label: 'Loan Processing' },
  { value: 'form-builder', label: 'Form Builder' },
  { value: 'rule-builder', label: 'Rule Builder' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'app-specific', label: 'App-Specific' },
  { value: 'advanced-features', label: 'Advanced Features' }
];

const DIFFICULTIES: { value: DifficultyLevel; label: string }[] = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' }
];

const USER_ROLES: { value: UserRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'lender', label: 'Lender' },
  { value: 'analyst', label: 'Analyst' },
  { value: 'viewer', label: 'Viewer' }
];

const CONTENT_TYPES: { value: ContentType; label: string }[] = [
  { value: 'text', label: 'Text Content' },
  { value: 'video', label: 'Video' },
  { value: 'interactive', label: 'Interactive' },
  { value: 'quiz', label: 'Quiz' }
];

const CreateTrainingGuidePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<CreateTrainingGuideRequest>({
    title: '',
    description: '',
    category: 'getting-started',
    targetRoles: [],
    difficulty: 'beginner',
    estimatedDuration: 30,
    content: {
      sections: [
        {
          id: 'section-1',
          title: '',
          content: '',
          type: 'text'
        }
      ]
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  // Check if user has permission to create training guides
  if (!user || !['admin', 'lender'].includes(user.role)) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            You don't have permission to create training guides.
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/training')}
          >
            Back to Training
          </Button>
        </Box>
      </Container>
    );
  }

  const handleInputChange = (field: keyof CreateTrainingGuideRequest, value: CreateTrainingGuideRequest[keyof CreateTrainingGuideRequest]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSectionChange = (sectionIndex: number, field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        sections: prev.content.sections.map((section, index) =>
          index === sectionIndex ? { ...section, [field]: value } : section
        )
      }
    }));
  };

  const addSection = () => {
    const newSectionId = `section-${formData.content.sections.length + 1}`;
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        sections: [
          ...prev.content.sections,
          {
            id: newSectionId,
            title: '',
            content: '',
            type: 'text'
          }
        ]
      }
    }));
  };

  const removeSection = (sectionIndex: number) => {
    if (formData.content.sections.length <= 1) return;
    
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        sections: prev.content.sections.filter((_, index) => index !== sectionIndex)
      }
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.title || !formData.description || formData.targetRoles.length === 0) {
        setError('Please fill in all required fields');
        return;
      }

      if (formData.content.sections.some(section => !section.title || !section.content)) {
        setError('Please complete all sections');
        return;
      }

      const newGuide = await trainingService.createTrainingGuide(formData);
      navigate(`/training/${newGuide.id}`);
    } catch (err) {
      setError('Failed to create training guide');
      console.error('Error creating training guide:', err);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    'Basic Information',
    'Content Sections',
    'Review & Create'
  ];

  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 0:
        return !!(formData.title && formData.description && formData.targetRoles.length > 0);
      case 1:
        return formData.content.sections.every(section => section.title && section.content);
      case 2:
        return true;
      default:
        return false;
    }
  };

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mt: 3, mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link color="inherit" href="/training" sx={{ cursor: 'pointer' }}>
            Training
          </Link>
          <Typography color="text.primary">Create Training Guide</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Create Training Guide
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create a new training guide to help users learn platform features.
            </Typography>
          </Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/training')}
            variant="outlined"
          >
            Cancel
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} orientation="horizontal">
          {steps.map((label, index) => (
            <Step key={label} completed={isStepComplete(index)}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Paper sx={{ p: 3 }}>
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Title *"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter training guide title"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description *"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe what users will learn in this training"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category"
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  >
                    {CATEGORIES.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    value={formData.difficulty}
                    label="Difficulty"
                    onChange={(e) => handleInputChange('difficulty', e.target.value)}
                  >
                    {DIFFICULTIES.map((difficulty) => (
                      <MenuItem key={difficulty.value} value={difficulty.value}>
                        {difficulty.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Estimated Duration (minutes)"
                  value={formData.estimatedDuration}
                  onChange={(e) => handleInputChange('estimatedDuration', parseInt(e.target.value))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Target Roles *</InputLabel>
                  <Select
                    multiple
                    value={formData.targetRoles}
                    label="Target Roles"
                    onChange={(e) => handleInputChange('targetRoles', e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as UserRole[]).map((value) => (
                          <Chip key={value} label={USER_ROLES.find(r => r.value === value)?.label} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {USER_ROLES.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Content Sections
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addSection}
                variant="outlined"
              >
                Add Section
              </Button>
            </Box>

            {formData.content.sections.map((section, index) => (
              <Accordion key={index} defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    Section {index + 1}: {section.title || 'Untitled'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <TextField
                        fullWidth
                        label="Section Title"
                        value={section.title}
                        onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
                        placeholder="Enter section title"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth>
                        <InputLabel>Content Type</InputLabel>
                        <Select
                          value={section.type}
                          label="Content Type"
                          onChange={(e) => handleSectionChange(index, 'type', e.target.value)}
                        >
                          {CONTENT_TYPES.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={8}
                        label="Content"
                        value={section.content}
                        onChange={(e) => handleSectionChange(index, 'content', e.target.value)}
                        placeholder={
                          section.type === 'text' 
                            ? "Enter content in Markdown format..." 
                            : "Enter content for this section..."
                        }
                        helperText={
                          section.type === 'text' 
                            ? "Use Markdown syntax for formatting (# for headings, **bold**, *italic*, etc.)"
                            : ""
                        }
                      />
                    </Grid>
                    {formData.content.sections.length > 1 && (
                      <Grid size={12}>
                        <Button
                          startIcon={<DeleteIcon />}
                          color="error"
                          onClick={() => removeSection(index)}
                        >
                          Remove Section
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Create
            </Typography>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {formData.title}
                </Typography>
                <Typography color="text.secondary" paragraph>
                  {formData.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip label={CATEGORIES.find(c => c.value === formData.category)?.label} size="small" />
                  <Chip label={DIFFICULTIES.find(d => d.value === formData.difficulty)?.label} size="small" />
                  <Chip label={`${formData.estimatedDuration} min`} size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Target Roles: {formData.targetRoles.map(role => 
                    USER_ROLES.find(r => r.value === role)?.label
                  ).join(', ')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sections: {formData.content.sections.length}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={() => setActiveStep(activeStep - 1)}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Training Guide'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => setActiveStep(activeStep + 1)}
                disabled={!isStepComplete(activeStep)}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateTrainingGuidePage;